import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, TrendingUp, Star, BarChart3, Brain, Shield, FileText } from 'lucide-react';

const taglines = [
  'Maximize Your Hotel Revenue',
  'Outsmart Your Competition',
  'AI-Powered Rate Intelligence',
];

export default function HeroSection() {
  const [currentTagline, setCurrentTagline] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/35449806/pexels-photo-35449806.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="City skyline at sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/50 via-transparent to-transparent" />
      </div>

      {/* Animated Particles/Grid Effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left stagger-children">
            <Badge 
              variant="outline" 
              className="mb-6 px-4 py-2 text-sm border-primary-foreground/30 text-primary-foreground/90 bg-primary-foreground/5 backdrop-blur-sm"
            >
              <span className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                AI-Powered Hotel Analytics Platform
              </span>
            </Badge>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6">
              <span className="block mb-2 transition-opacity duration-500">
                {/* {taglines[currentTagline]} */}
                 Hotel Price Intelligence & Competitor Analysis 
              </span>
              <span className="block text-accent-light">
                With Real-Time Intelligence
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-primary-foreground/80 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Stay ahead with real-time competitor tracking, OTA parity monitoring, market demand analysis, and AI-driven pricing insights—all in one smart dashboard.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <Button 
                variant="heroPrimary" 
                size="xl" 
                className="group"
                onClick={() => navigate('/register')}
              >
                Sign Up Free
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button 
                variant="hero" 
                size="xl" 
                className="group"
                onClick={() => navigate('/login')}
              >
                Sign In
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-6 justify-center lg:justify-start text-primary-foreground/70 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Rate Parity Monitoring</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Brain className="w-4 h-4" />
                <span>Prowen AI Insights</span>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>PDF Reports</span>
              </div>
            </div>
          </div>

          {/* Right Content - Dashboard Preview */}
          <div className="relative">
            <div className="relative glass-card-dark rounded-2xl p-1 shadow-2xl animate-float">
              {/* Dashboard Mock */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-4 sm:p-6">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="text-xs text-slate-400">Prowen Analytics Dashboard</span>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Rate Parity', value: '94%', trend: '+2.1%', color: 'text-emerald-400' },
                    { label: 'Avg Rate', value: '₹8,500', trend: '+8%', color: 'text-blue-400' },
                    { label: 'Market Rank', value: '#2', trend: '+1', color: 'text-purple-400' },
                    { label: 'OTA Score', value: '4.7', trend: '+0.3', color: 'text-amber-400' },
                  ].map((kpi, index) => (
                    <div key={index} className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">{kpi.label}</p>
                      <p className="text-xl font-bold text-slate-100">{kpi.value}</p>
                      <p className={`text-xs ${kpi.color}`}>{kpi.trend}</p>
                    </div>
                  ))}
                </div>

                {/* Chart Placeholder */}
                <div className="bg-slate-800/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-slate-300">OTA Rate Comparison</span>
                    <span className="text-xs text-slate-500">Last 7 days</span>
                  </div>
                  <div className="flex items-end gap-2 h-24">
                    {[65, 45, 78, 56, 89, 72, 85].map((height, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all duration-500"
                          style={{ height: `${height}%` }}
                        />
                        <span className="text-[10px] text-slate-500">{['M','T','W','T','F','S','S'][i]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom Stats */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">AI Insight: <span className="text-emerald-400 font-medium">Increase rates by 8%</span></span>
                  <span className="text-slate-400">Updated: <span className="text-slate-300">Live</span></span>
                </div>
              </div>
            </div>

            {/* Floating Cards */}
            <div className="absolute -top-4 -right-4 glass-card rounded-xl p-3 shadow-lg animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Brain className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">AI Analysis</p>
                  <p className="text-sm font-semibold text-foreground">Prowen AI Powered</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -left-4 glass-card rounded-xl p-3 shadow-lg animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rate Parity</p>
                  <p className="text-sm font-semibold text-foreground">3 Undercuts Found</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-primary-foreground/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-primary-foreground/50 rounded-full" />
        </div>
      </div>
    </section>
  );
}
